// The shared kit: small presentational pieces every screen is built from.
// They read design tokens (--fsp-*) and never fetch data.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  badgeEmoji,
  botFace,
  type FaceMood,
  type GameStatus,
  type PlayerSummary,
  type TeamBadge,
  type TeamRef,
  type WeeklyHat,
} from '@fsp/domain';
import { onColor } from '@fsp/theme';
import { BadgeGlyph, Icon } from './Icon';
import { FaceArt, HatArt } from './FaceArt';
import { useTeamHat } from './hat';
import { Term } from './Why';
import { clubLabel, positionLabelOf, proTeamOf } from '../sports/registry';

// ── Position pill ─────────────────────────────────────────────────────────

/**
 * How a position reads on screen. One place, so a pill and a filter chip
 * agree, and the sport's to say (`positionLabelOf`): it was a literal here,
 * `DST` to `DEF`, which the screens that skipped it showed as DST.
 */
export function positionLabel(pos: string): string {
  return positionLabelOf(pos);
}

export function PositionPill({ pos, size = 'md' }: { pos: string; size?: 'sm' | 'md' }) {
  const token = `--fsp-pos-${pos.toLowerCase()}`;
  // The position's color is data, handed to the stylesheet as `--fsp-pill`;
  // how a pill wears it — a tint in Clean, a crayon outline in Crayon — is the
  // look's (kit.css, looks/<id>/look.css).
  return (
    <span
      className={`fsp-pill fsp-pill-${size}`}
      style={{ '--fsp-pill': `var(${token}, var(--fsp-ink-secondary))` } as CSSProperties}
    >
      {positionLabel(pos)}
    </span>
  );
}

/**
 * A lineup slot's position badge as the button that moves its player (B-275).
 * It is where Sleeper, ESPN and Yahoo all put the swap, so it is where an
 * experienced player reaches first (UX_STANDARDS.md, *Conventions to follow*),
 * and it is drawn as a control, because a badge that only looks like a label
 * is how a swap went unfound.
 *
 * `state` is the badge's part in a move: the one picked, one it can go to,
 * one it cannot, or a slot whose game has started. Its name starts with the
 * words on it, so a screen reader and the eye agree (WCAG 2.5.3).
 */
export function SlotButton({
  pos,
  action,
  state = null,
  size = 'md',
  word = null,
  onClick,
}: {
  pos: string;
  /**
   * What a press does, after the slot's own words: "move Brock Purdy". It
   * starts with `word` when there is one, so the words on the button are its
   * name's first words (WCAG 2.5.3).
   */
  action: string;
  state?: 'picked' | 'target' | 'dim' | 'locked' | null;
  size?: 'sm' | 'md';
  /**
   * The press in a word beside the badge — Move, Here, Cancel, Fill — where a
   * screen has the room (B-288, DIRECTION U7). Without one, a small up-down
   * arrow under the badge says it moves.
   */
  word?: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      // Laid out for a word whenever the screen has them, a locked badge too,
      // so every badge in the column lines up.
      className={`fsp-slot-btn${word !== null ? ' fsp-slot-btn-word' : ''}`}
      data-state={state ?? undefined}
      aria-pressed={state === 'picked'}
      aria-label={`${positionLabel(pos)}: ${action}`}
      onClick={(e) => {
        // The row around it has its own tap (the card, or a move's end); the
        // badge's press is the badge's alone.
        e.stopPropagation();
        onClick();
      }}
    >
      <PositionPill pos={pos} size={size} />
      {/* A LOCKED BADGE OFFERS NOTHING, so it carries no cue. The space keeps
          the words apart for anyone reading them as text ("QB Move"). */}
      {state === 'locked' ? null : word !== null ? (
        word ? (
          <>
            {' '}
            <span className="fsp-slot-word">{word}</span>
          </>
        ) : null
      ) : (
        <span className="fsp-slot-cue" aria-hidden>
          <Icon name="move" size="xs" />
        </span>
      )}
    </button>
  );
}

// ── Team mark ─────────────────────────────────────────────────────────────

export function TeamMark({
  team,
  size = 28,
  face = 'idle',
  hat: given,
}: {
  /**
   * The badge, when the team has one, takes the initials' place (DIRECTION
   * U25, B-440); a face its manager built (B-488) or, on a bot's seat, its
   * character's face (B-464).
   */
  team: Pick<TeamRef, 'abbreviation' | 'colors'> & { badge?: TeamBadge; id?: string };
  size?: number;
  /** What a face is doing (AI_MANAGERS.md §5.1); idle unless a screen says. */
  face?: FaceMood;
  /**
   * The hat it wears, where a screen says which (a chat card's, as posted);
   * absent, the week's hat it wears now, if any.
   */
  hat?: WeeklyHat | null;
}) {
  const badge = team.badge ?? null;
  const emoji = badgeEmoji(badge);
  // THE WEEK'S HAT (PLAN-2026-10-30-the-weekly-hat §7), perched on the disc,
  // and the face under it bare-headed; not on a mark too small to show it.
  const worn = useTeamHat(team.id);
  const hat = given === undefined ? worn : given;
  const perched = hat !== null && size >= 20 ? hat : null;
  // A bot dresses up for a season, by today's date where it is drawn (B-490).
  const own =
    badge?.kind === 'bot'
      ? botFace(badge.id, new Date())
      : badge?.kind === 'face'
        ? badge.face
        : null;
  const drawn = own !== null && perched !== null ? { ...own, headwear: 'none' as const } : own;
  return (
    // A team's colors are data, handed over as custom properties; how the mark
    // wears them (a ringed disc in Clean, a crayon circle in Crayon) is the look's.
    <span
      className="fsp-teammark"
      style={
        {
          width: size,
          height: size,
          fontSize: Math.round(size * 0.36),
          '--fsp-team': team.colors.primary,
          '--fsp-team-2': team.colors.secondary,
          '--fsp-team-on': onColor(team.colors.primary),
        } as CSSProperties
      }
      data-badge={badge?.kind}
      data-hat={perched ?? undefined}
      // What it is doing, for a motion the whole mark makes whatever its
      // badge (a dance, kit.css).
      data-face={face === 'idle' ? undefined : face}
      aria-hidden
    >
      {perched !== null ? (
        <span className="fsp-teammark-hat">
          <HatArt hat={perched} width={Math.round(size * 1.05)} />
        </span>
      ) : null}
      {drawn !== null ? (
        // A FACE (B-464, B-488), filling the disc: the mark is the character.
        // An outfit nobody painted wears the team's colors (B-493).
        <FaceArt face={drawn} mood={face} team={team.colors} />
      ) : emoji !== null ? (
        // The system's emoji font draws it, a little under the disc's width.
        <span className="fsp-teammark-emoji" style={{ fontSize: Math.round(size * 0.58) }}>
          {emoji}
        </span>
      ) : badge?.kind === 'icon' ? (
        // The look's own drawing, as its awards and achievements are drawn.
        <BadgeGlyph icon={badge.id} size={Math.round(size * 0.62)} />
      ) : (
        team.abbreviation.slice(0, 3)
      )}
      {badge?.kind === 'bot' && drawn !== null ? (
        // A BOT IS MARKED AS A BOT (B-488): once a person can wear a face, a
        // face alone no longer says so. A tag at the lower right, where a
        // status dot sits on an avatar, on every screen that draws a team.
        <span className="fsp-teammark-bot">
          <Icon name="bot" size="xs" />
        </span>
      ) : null}
    </span>
  );
}

// ── Pro team code ─────────────────────────────────────────────────────────

/**
 * A player's club beside his name (B-510): its abbreviation, the club's name
 * when a pointer rests on it, and FA for a player on no club.
 */
export function ProTeamCode({ code }: { code: string | null }) {
  if (!code)
    return (
      <abbr className="fsp-proteam fsp-proteam-none" title="Free agent: on no club">
        FA
      </abbr>
    );
  const team = proTeamOf(code);
  return (
    <abbr className="fsp-proteam" title={team?.name}>
      {team?.abbreviation ?? code}
    </abbr>
  );
}

/** What rides beside a player's name in a row: his club, then his status (B-510). */
export function PlayerMarks({ player }: { player: PlayerSummary }) {
  return (
    <>
      <ProTeamCode code={player.proTeam} />
      <StatusBadge player={player} />
    </>
  );
}

// ── Live dot ──────────────────────────────────────────────────────────────

export function LiveDot({ label }: { label?: string }) {
  return (
    <span className="fsp-live" aria-label="Live">
      <span className="fsp-live-dot" />
      {label ? <span className="fsp-live-label">{label}</span> : null}
    </span>
  );
}

// ── Injury / status ───────────────────────────────────────────────────────

export function StatusBadge({ player }: { player: PlayerSummary }) {
  if (player.onBye) return <span className="fsp-status fsp-status-bye">BYE</span>;
  if (!player.injury || player.injury.severity === 'none') return null;
  const cls =
    player.injury.severity === 'out'
      ? 'fsp-status-out'
      : player.injury.severity === 'likely-out'
        ? 'fsp-status-doubt'
        : 'fsp-status-warn';
  return (
    <span className={`fsp-status ${cls}`} title={player.injury.name}>
      {player.injury.status}
    </span>
  );
}

// ── Game label ────────────────────────────────────────────────────────────

/**
 * A kickoff's words with its zone and an hour's ":00" apart, so a phone can
 * leave them off (B-279): every time on the site is the reader's own zone,
 * which the account says, and "Sun 11:00 AM MDT" cut a phone's row to
 * "Sun 11:00 A". A phone reads "Sun 11 AM", and "Sun 2:25 PM" whole.
 */
function Kickoff({ label }: { label: string }) {
  const m = /^(.*?\d{1,2})(:\d{2})(\s?[AP]M)?(\s[A-Z]{2,5})?$/.exec(label);
  if (!m) return <>{label}</>;
  const [, head, minutes, half = '', zone = ''] = m;
  // One box: a game label lays its pieces out with a gap between them, and
  // the time's pieces apart read "Sun 11 :00 AM".
  return (
    <span>
      {head}
      {minutes === ':00' ? <span className="fsp-kick-minutes">{minutes}</span> : minutes}
      {half}
      {zone ? <span className="fsp-zone">{zone}</span> : null}
    </span>
  );
}

/**
 * A player's game: "vs SF · Sun 1 PM", live, final, or his bye. His own club
 * is beside his name (`ProTeamCode`, B-510), so the label names only the
 * other one.
 */
export function GameLabel({ game }: { game: GameStatus | null }) {
  if (!game) return <span className="fsp-muted">—</span>;
  if (game.state === 'bye') return <span className="fsp-muted">Bye</span>;
  const vs = game.opponent ? `${game.home ? 'vs' : 'at'} ${clubLabel(game.opponent)}` : '';
  // A GAME UNDER WAY IS "LIVE" ONLY WITH A RUNNING SCORE — a live feed's,
  // which the simulator has and a real league will once one exists (BACKLOG
  // L0). Without one, all anybody knows is that it kicked off: the schedule
  // calls a game final only in the morning's pull, and a pulsing "Live" sat on
  // a Thursday game until Friday morning, hours after it ended (round 3's
  // review, SCREENS-4; B-171).
  if (game.state === 'live' && game.score === null) {
    return (
      <span className="fsp-gamelabel fsp-muted">
        Kicked off <Kickoff label={game.label} />
      </span>
    );
  }
  if (game.state === 'live') {
    return (
      <span className="fsp-gamelabel">
        <LiveDot />
        <span>{game.label}</span>
        {game.score ? <span className="fsp-muted"> · {game.score}</span> : null}
      </span>
    );
  }
  if (game.state === 'final') {
    return (
      <span className="fsp-gamelabel fsp-muted">Final{game.score ? ` · ${game.score}` : ''}</span>
    );
  }
  return (
    <span className="fsp-gamelabel fsp-muted">
      {vs}
      <span className="fsp-gamelabel-when">
        <span className="fsp-gamelabel-sep">{' · '}</span>
        <Kickoff label={game.label} />
      </span>
    </span>
  );
}

// ── Score number with count-up and glow ───────────────────────────────────

export function useCountUp(value: number, ms = 300): number {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    const from = fromRef.current;
    if (from === value) return;
    const start = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / ms);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(from + (value - from) * eased);
      if (k < 1) raf.current = requestAnimationFrame(step);
      else fromRef.current = value;
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [value, ms]);
  return display;
}

export function ScoreNumber({
  value,
  decimals = 1,
  size = 40,
  color,
  glow = true,
  style,
}: {
  value: number;
  decimals?: number;
  size?: number;
  color?: string;
  glow?: boolean;
  style?: CSSProperties;
}) {
  const shown = useCountUp(value);
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      if (glow) {
        setFlash(true);
        const t = setTimeout(() => setFlash(false), 700);
        return () => clearTimeout(t);
      }
    }
  }, [value, glow]);
  return (
    <span
      className={`fsp-num fsp-disp fsp-score ${flash ? 'fsp-glow' : ''}`}
      style={
        {
          // The person's text size reaches the big numbers too (B-205).
          fontSize: `calc(${size}px * var(--fsp-font-scale, 1))`,
          lineHeight: 1,
          // Handed over as data rather than set, so a look can draw the score
          // in its own colors (`.fsp-score`, kit.css).
          '--fsp-score': color ?? 'var(--fsp-ink-primary)',
          ...style,
        } as CSSProperties
      }
    >
      {shown.toFixed(decimals)}
    </span>
  );
}

// ── Delta chip ────────────────────────────────────────────────────────────

export function DeltaChip({ points, label }: { points: number; label?: string }) {
  const up = points >= 0;
  const chip = (
    <span className={`fsp-delta ${up ? 'fsp-delta-up' : 'fsp-delta-down'} fsp-pop`}>
      {up ? '+' : ''}
      {points.toFixed(1)}
    </span>
  );
  // What it was for, when asked (B-374): a native title never reached a finger.
  return label === undefined ? (
    chip
  ) : (
    <Term tip={label} name={`${up ? '+' : ''}${points.toFixed(1)}`} className="fsp-matchup-term">
      {chip}
    </Term>
  );
}

// ── Layout helpers ────────────────────────────────────────────────────────

export function Cap({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="fsp-cap" style={style}>
      {children}
    </div>
  );
}

export function Surface({
  children,
  style,
  className,
  onClick,
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  /** A clickable surface renders as a button-like region (the Home pulse opens chat). */
  onClick?: () => void;
}) {
  return (
    <div
      className={`fsp-surface ${className ?? ''}`}
      style={style}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' ? onClick() : undefined) : undefined}
    >
      {children}
    </div>
  );
}

export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="fsp-empty">
      <div>{children}</div>
      {action}
    </div>
  );
}
